import React, { useState, useEffect } from "react";
import confetti from "canvas-confetti";

interface MysteryBoxProps {
    onComplete: () => void;
}

// Définition des configurations des tiers
const tierConfigs = [
    { range: [1, 9], step: 2, chances: 9400000 },
    { range: [2, 8], step: 2, chances: 500000 },
    { range: [10, 15], step: 5, chances: 100000 },
    { range: [20, 45], step: 5, chances: 25000 },
    { range: [50, 80], step: 5, chances: 5000 },
    { range: [85, 175], step: 25, chances: 5000 },
    { range: [200, 775], step: 25, chances: 1000 },
    { range: [800, 1600], step: 50, chances: 500 },
    { range: [1700, 2300], step: 100, chances: 450 },
    { range: [2400, 2800], step: 100, chances: 350 },
    { range: [2900, 3200], step: 100, chances: 300 },
    { range: [3300, 3700], step: 100, chances: 250 },
    { range: [3800, 4200], step: 100, chances: 200 },
    { range: [4300, 4700], step: 100, chances: 150 },
    { range: [4800, 5400], step: 100, chances: 100 },
    { range: [5500, 6500], step: 100, chances: 50 },
    { range: [6600, 9900], step: 100, chances: 25 },
    { range: [10000, 10000], step: 1, chances: 1 },
];

// Fonction pour obtenir une récompense aléatoire en fonction des tiers
function getRandomReward(): { label: string; value: number | string } {
    const totalChances = tierConfigs.reduce(
        (acc, config) => acc + config.chances,
        0,
    );
    const randomChance = Math.floor(Math.random() * totalChances);

    let currentChance = 0;
    for (const config of tierConfigs) {
        currentChance += config.chances;
        if (randomChance < currentChance) {
            const min = config.range[0];
            const max = config.range[1];
            const step = config.step;

            const values = [];
            for (let i = min; i <= max; i += step) {
                values.push(i);
            }

            const value = values[Math.floor(Math.random() * values.length)];
            return { label: value.toString(), value };
        }
    }
    return { label: "Error", value: 0 };
}

const MysteryBox: React.FC<MysteryBoxProps> = ({ onComplete }) => {
    const handleClick = () => {
        onComplete(); // Appeler la fonction onComplete lorsqu'on clique sur la mini-box
    };
    const [openedIndex, setOpenedIndex] = useState<number | null>(null);
    const [reward, setReward] = useState<{
        label: string;
        value: number | string;
    } | null>(null);
    const [attemptsLeft, setAttemptsLeft] = useState<number>(4); // Nombre de tentatives restantes (initialisé à 4)
    const [lastOpened, setLastOpened] = useState<Date | null>(null);
    const [nextTryTime, setNextTryTime] = useState<Date | null>(null);
    const [cooldownTimeLeft, setCooldownTimeLeft] = useState<number>(0);
    const [showResult, setShowResult] = useState(false); // Afficher le résultat
    const [flipped, setFlipped] = useState(false); // Flip state

    // Fonction de gestion du clic sur une mini-box
    const handleMiniBoxClick = () => {
        setShowResult(true); // Affiche le résultat après avoir cliqué
    };

    // Initialiser les tentatives à 0 au chargement
    useEffect(() => {
        setAttemptsLeft(4); // Initialiser les tentatives à 4
    }, []);

    // Mise à jour des tentatives après avoir ouvert un coffre
    const handleOpenBox = (index: number) => {
        if (attemptsLeft <= 0 || openedIndex !== null) {
            return;
        }

        const result = getRandomReward();
        setOpenedIndex(index);
        setReward(result);

        // Mise à jour des tentatives restantes
        setAttemptsLeft((prev) => Math.max(prev - 1, 0)); // Décrémenter les tentatives
        setLastOpened(new Date());

        // Définir la prochaine tentative dans 6 heures
        const nextTry = new Date();
        nextTry.setHours(nextTry.getHours() + 6);
        setNextTryTime(nextTry);

        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    };

    // Affichage du message de cooldown après avoir ouvert un coffre
    useEffect(() => {
        if (lastOpened) {
            const interval = setInterval(() => {
                const totalSeconds = Math.max(
                    0,
                    Math.floor((nextTryTime!.getTime() - Date.now()) / 1000),
                );
                if (totalSeconds <= 0) {
                    clearInterval(interval);
                }
            }, 1000);
        }
    }, [lastOpened, nextTryTime]);

    // Ferme automatiquement la box après 5 secondes
    useEffect(() => {
        if (openedIndex !== null) {
            const timer = setTimeout(() => {
                setFlipped(false); // retourne la box
                setOpenedIndex(null); // réinitialise l'état
                setReward(null); // (optionnel)
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [openedIndex]);

    useEffect(() => {
        if (!nextTryTime) return;

        const interval = setInterval(() => {
            const totalSeconds = Math.max(
                0,
                Math.floor((nextTryTime.getTime() - Date.now()) / 1000),
            );
            setCooldownTimeLeft(totalSeconds);
            if (totalSeconds <= 0) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [nextTryTime]);

    return (
        <div className="box flex flex-col items-center justify-center py-0 px-2 h-full min-h-[250px]">
            <div className="-mt-5 sm:!text-sm md:!text-base font-semibold text-black">
                Box Mystère
            </div>
            <div className="sm:!text-sm md:!text-sm text-gray-600 mt-1 mb-3">
                Selectionnez une box.
            </div>

            <div className="flex justify-center gap-1 mt-0 mb-3 w-full">
                {[0, 1, 2].map((index) => (
                    <div
                        key={index}
                        className={`relative w-20 h-20 sm:!w-20 sm:!h-16 rounded-lg overflow-hidden shadow-xl border transition-transform duration-300 ${openedIndex === index ? "scale-105" : "hover:scale-[1.05]"} flip-container`}
                        onClick={() => handleOpenBox(index)}
                    >
                        <div
                            className={`flipper ${openedIndex === index ? "flipped" : ""}`}
                        >
                            {/* FRONT */}
                            <div className="front">
                                <img
                                    src="/dist/image/GifDefi.jpg"
                                    alt="Mystery Box"
                                    className="w-[100%] h-[100%] object-cover items-center justify-center sm:p-1 md:!p-0"
                                />
                            </div>

                            {/* BACK */}
                            <div className="back flex items-center justify-center bg-white">
                                {reward && openedIndex === index && (
                                    <div className="flex items-center justify-center px-2 py-1 rounded-md">
                                        <span className="text-black text-sm font-bold">
                                            {reward.label}
                                        </span>
                                        <img
                                            src="/dist/image/Fav-Infinexia-512x512.webp"
                                            className="w-4 h-4 ml-1"
                                            alt="icon"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Affichage du message de cooldown après avoir ouvert un coffre */}
            <div className="pt-1">
                {attemptsLeft < 4 && nextTryTime && cooldownTimeLeft > 0 && (
                    <div className="mt-0 text-sm text-black text-center font-medium">
                        <div className="text-sm text-gray-600 font-normal">
                            Vous pouvez réessayer dans{" "}
                        </div>
                        {(() => {
                            const hours = Math.floor(cooldownTimeLeft / 3600);
                            const minutes = Math.floor(
                                (cooldownTimeLeft % 3600) / 60,
                            );
                            const seconds = cooldownTimeLeft % 60;
                            return `${hours}h ${minutes}m ${seconds}s`;
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MysteryBox;
